/** @jsxImportSource @emotion/react */

import { toast } from '@app/components/ui/Toast/toast'
import { useUpsertLayerMetadata } from '@app/domains/layers/hooks/useUpsertLayerMetadata'
import { userAtom } from '@app/hooks/useUserProfile'
import { UserRole } from '@app/types/userTypes'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { DataLayerDetailOutletContext } from '../types'
import { useElementInfoDetail } from '../hooks/useElementInfoDetail'

const CONFIDENCE_OPTIONS = ['A', 'B', 'C', 'D', 'E'] as const

const formatPercentage = (fraction: number) => `${Math.round(fraction * 100)}%`

export const DataLayerDetailAttributes = () => {
  const { data: user } = useAtomValue(userAtom)
  const isAdmin = user?.user_roles.includes(UserRole.ADMIN)

  const { layer, layerMeta } = useOutletContext<DataLayerDetailOutletContext>()
  const { mutate: upsertLayerMetadata, isPending } = useUpsertLayerMetadata()
  const elementLayerId = layer?.type === 'Element' ? layer.element.type : undefined
  const { data: elementInfoDetail } = useElementInfoDetail(elementLayerId)

  const attributes = layerMeta?.metadata?.attributes ?? {}
  const attributeEntries = Object.entries(attributes).sort((a, b) => a[0].localeCompare(b[0]))
  const coverage = elementInfoDetail?.coverage

  const [isEditing, setIsEditing] = useState(false)
  const [draftConfidence, setDraftConfidence] = useState<Record<string, string>>({})

  const startEditing = () => {
    setDraftConfidence(
      Object.fromEntries(
        attributeEntries.map(([attribute, { confidence }]) => [attribute, confidence]),
      ),
    )
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
  }

  const saveEditing = () => {
    if (!layerMeta?.metadata || !layerMeta.id) return

    const { layer_id: _layerId, ...metadataWithoutLayerId } = layerMeta.metadata

    upsertLayerMetadata(
      {
        layerId: layerMeta.id,
        body: {
          ...metadataWithoutLayerId,
          attributes: Object.fromEntries(
            attributeEntries.map(([attribute]) => [
              attribute,
              { confidence: draftConfidence[attribute] },
            ]),
          ),
        },
      },
      {
        onSuccess: () => setIsEditing(false),
        onError: () =>
          toast({
            title: 'Error',
            description: 'Failed to save attribute confidence',
            position: 'top-center',
            type: 'error',
          }),
      },
    )
  }

  return (
    <Box sx={{ marginTop: 2 }}>
      {isAdmin && attributeEntries.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
          {isEditing ? (
            <>
              <Button variant="outlined" size="small" disabled={isPending} onClick={cancelEditing}>
                Cancel
              </Button>
              <Button variant="contained" size="small" disabled={isPending} onClick={saveEditing}>
                {isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={startEditing}
            >
              Edit
            </Button>
          )}
        </Box>
      )}
      {attributeEntries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No attributes available.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Attribute</TableCell>
                <TableCell>Confidence</TableCell>
                {coverage && (
                  <>
                    <TableCell>Source Coverage</TableCell>
                    <TableCell>Treatment</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {attributeEntries.map(([attribute, { confidence }]) => {
                const attributeCoverage = coverage?.[attribute]

                return (
                  <TableRow key={attribute}>
                    <TableCell>{attribute}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Select
                          size="small"
                          value={draftConfidence[attribute] ?? ''}
                          onChange={(e) =>
                            setDraftConfidence((prev) => ({
                              ...prev,
                              [attribute]: e.target.value,
                            }))
                          }
                        >
                          {CONFIDENCE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        confidence
                      )}
                    </TableCell>
                    {coverage && (
                      <>
                        <TableCell>
                          {attributeCoverage
                            ? formatPercentage(attributeCoverage.not_null.fraction)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {attributeCoverage ? (
                            <Typography variant="body2" component="div">
                              {attributeCoverage.treatment}
                            </Typography>
                          ) : (
                            <Typography variant="body2" component="div">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default DataLayerDetailAttributes
